import React, { useMemo } from 'react';
import type { Dispatch } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import type { TaskPages } from '../tasksDefinitions.ts';

import { CheckboxSelectFilter } from './CheckboxSelectFilter.tsx';
import { FilterKeys } from './filtersDefinitions.ts';
import type { FiltersState } from './useFiltersReducer.ts';

const i18nBase = defineMessages({
	pageLabel: {
		id: 'home.views.tasks.filters.label.page',
		defaultMessage: 'Page',
		description: 'The label for a selector to select a list of pages.',
	},
});

const i18nLiveDocs = defineMessages({
	pageLabel: {
		id: 'home.views.tasks.filters.label.page.livedocs',
		defaultMessage: 'Page or Doc',
		description: 'Updated version of home.views.tasks.filters.label.page',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

interface SelectPageProps {
	options: TaskPages;
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const SelectPage = ({ options, filterDispatch, filterState }: SelectPageProps) => {
	const { formatMessage } = useIntl();

	const checkboxOptions = useMemo(
		() =>
			Object.keys(options).map((spaceId: string) => ({
				label: options?.[spaceId]?.title ?? ('' as string),
				value: options?.[spaceId]?.pageId ?? ('' as string),
			})),
		[options],
	);

	const getSelectedValue = () =>
		// Using the filter values from state, match value from options formatted above.
		(filterState.filters[FilterKeys.PAGE] ?? []).reduce(
			(current, statePageId) => {
				const match = checkboxOptions.find(
					(checkboxOption) => checkboxOption.value === statePageId,
				);

				if (match) {
					current.push(match);
				}

				return current;
			},
			[] as { label: string; value: string }[],
		);

	return (
		<CheckboxSelectFilter
			options={checkboxOptions}
			filterKey={FilterKeys.PAGE}
			label={formatMessage(i18n.pageLabel)}
			inputId="page-filter"
			filterDispatch={filterDispatch}
			selectedValues={getSelectedValue()}
		/>
	);
};
